import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Building } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BankAccountManager = ({ open, onOpenChange, accounts, onAccountChange }) => {
  const [editingAccount, setEditingAccount] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteAccount, setDeleteAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    account_number: "",
    currency: "EUR"
  });
  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setFormData({ name: "", account_number: "", currency: "EUR" });
    setErrors({});
    setEditingAccount(null);
    setShowForm(false);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.name.length < 3 || formData.name.length > 50) {
      newErrors.name = "Name must be 3-50 characters";
    }
    
    if (formData.account_number.length < 5 || formData.account_number.length > 30) {
      newErrors.account_number = "Account number must be 5-30 characters";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      if (editingAccount) {
        await axios.put(`${API}/bank-accounts/${editingAccount.id}`, formData);
        toast.success("Account updated successfully");
      } else {
        await axios.post(`${API}/bank-accounts`, formData);
        toast.success("Account created successfully");
      }
      
      onAccountChange();
      resetForm();
    } catch (err) {
      console.error("Error saving account:", err);
      toast.error(editingAccount ? "Failed to update account" : "Failed to create account");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (account) => {
    setFormData({
      name: account.name,
      account_number: account.account_number,
      currency: account.currency
    });
    setEditingAccount(account);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deleteAccount) return;
    
    try {
      await axios.delete(`${API}/bank-accounts/${deleteAccount.id}`);
      toast.success("Account deleted successfully");
      onAccountChange();
    } catch (err) {
      console.error("Error deleting account:", err);
      toast.error("Failed to delete account");
    } finally {
      setDeleteAccount(null);
    }
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg" data-testid="account-manager-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">Bank Accounts</DialogTitle>
            <DialogDescription>
              Manage your bank accounts for balance tracking
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Account List */}
            {!showForm && (
              <>
                <div className="space-y-2 max-h-[300px] overflow-auto">
                  {accounts.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground" data-testid="no-accounts-message">
                      <Building className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No accounts yet</p>
                    </div>
                  ) : (
                    accounts.map(account => (
                      <div
                        key={account.id}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                        data-testid={`account-item-${account.id}`}
                      >
                        <div>
                          <p className="font-medium">{account.name}</p>
                          <p className="text-sm text-muted-foreground font-mono">
                            {account.account_number} • {account.currency}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(account)}
                            className="h-8 w-8"
                            data-testid={`edit-account-${account.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteAccount(account)}
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            data-testid={`delete-account-${account.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <Button
                  onClick={() => setShowForm(true)}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                  data-testid="add-account-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Account
                </Button>
              </>
            )}

            {/* Account Form */}
            {showForm && (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="account-form">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">
                    {editingAccount ? "Edit Account" : "New Account"}
                  </h3>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={resetForm}
                    className="h-8 w-8"
                    data-testid="cancel-form-button"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Account Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Business Checking"
                    className={errors.name ? "border-destructive" : ""}
                    data-testid="account-name-input"
                  />
                  {errors.name && (
                    <p className="text-xs text-destructive">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="account_number">Account Number *</Label>
                  <Input
                    id="account_number"
                    value={formData.account_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, account_number: e.target.value }))}
                    placeholder="e.g., DE89370400440532013000"
                    className={errors.account_number ? "border-destructive" : ""}
                    data-testid="account-number-input"
                  />
                  {errors.account_number && (
                    <p className="text-xs text-destructive">{errors.account_number}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Input
                    id="currency"
                    value={formData.currency}
                    disabled
                    className="bg-muted"
                    data-testid="account-currency-input"
                  />
                  <p className="text-xs text-muted-foreground">EUR only for MVP</p>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                    className="flex-1 rounded-full"
                    data-testid="cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                    data-testid="save-account-button"
                  >
                    {saving ? "Saving..." : editingAccount ? "Update" : "Create"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteAccount} onOpenChange={() => setDeleteAccount(null)}>
        <AlertDialogContent data-testid="delete-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteAccount?.name}"? 
              This will also delete all associated balance records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BankAccountManager;
